import React, { useMemo } from 'react';
import { Order, PaginationInfo } from '../../lib/types';
import { EmptyState } from '../ui';
import { OrderRow } from './OrderRow';
import { SortColumn, SortDir, STATUS_ORDER } from './types';
import type { OrderAuditEntry } from '../../lib/api';

interface OrdersTableProps {
  orders:    Order[];
  currency:  string;
  locale:    string;
  sortCol:   SortColumn | null;
  sortDir:   SortDir;
  onSort:    (col: SortColumn) => void;

  expandedId:   string | null;
  onToggleExpand: (id: string) => void;
  onStatusClick:  (order: Order, e: React.MouseEvent) => void;
  onMenuClick:    (order: Order, e: React.MouseEvent) => void;

  auditOrderId: string | null;
  auditLoading: boolean;
  auditLog:     OrderAuditEntry[];
  onToggleAudit: (id: string) => void;

  serverPagination?: PaginationInfo;
  onPageChange?:     (page: number) => void;

  onAddOrder?: () => void;
}

function SortTh({
  col, label, style, sortCol, sortDir, onSort,
}: {
  col: SortColumn; label: string; style?: React.CSSProperties;
  sortCol: SortColumn | null; sortDir: SortDir; onSort: (c: SortColumn) => void;
}) {
  const isActive = sortCol === col;
  const arrow = isActive ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th
      onClick={() => onSort(col)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
      title={`Sort by ${label}`}
    >
      {label}
      {arrow && <span style={{ fontSize: 8, marginLeft: 3, opacity: .7 }}>{arrow}</span>}
    </th>
  );
}

export function OrdersTable(props: OrdersTableProps) {
  const {
    orders, currency, locale, sortCol, sortDir, onSort,
    expandedId, onToggleExpand, onStatusClick, onMenuClick,
    auditOrderId, auditLoading, auditLog, onToggleAudit,
    serverPagination, onPageChange, onAddOrder,
  } = props;

  const sortedOrders = useMemo(() => {
    if (!sortCol) return orders;
    return [...orders].sort((a, b) => {
      let va: string | number;
      let vb: string | number;
      switch (sortCol) {
        case 'order_date':      va = a.order_date || '';                   vb = b.order_date || ''; break;
        case 'department_name': va = (a.department_name || '').toLowerCase(); vb = (b.department_name || '').toLowerCase(); break;
        case 'vendor':          va = (a.vendor || '').toLowerCase();        vb = (b.vendor || '').toLowerCase(); break;
        case 'order_number':    va = a.order_number || '';                  vb = b.order_number || ''; break;
        case 'status':          va = STATUS_ORDER[a.status || ''] ?? 99;   vb = STATUS_ORDER[b.status || ''] ?? 99; break;
        case 'total_cost':      va = a.total_cost || 0;                    vb = b.total_cost || 0; break;
        default: return 0;
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1  : -1;
      return 0;
    });
  }, [orders, sortCol, sortDir]);

  const sPage       = serverPagination?.page ?? 1;
  const sLimit      = serverPagination?.limit ?? 50;
  const sTotal      = serverPagination?.total ?? sortedOrders.length;
  const sTotalPages = serverPagination?.total_pages ?? 1;
  const startIdx    = (sPage - 1) * sLimit;

  function Pagination() {
    if (sTotal <= sLimit && !serverPagination) return null;
    const showingFrom = sTotal === 0 ? 0 : startIdx + 1;
    const showingTo   = Math.min(startIdx + sortedOrders.length, sTotal);
    return (
      <div
        className="flex items-center justify-between"
        style={{ padding: '10px 0', fontSize: 'var(--fs-xs)', color: 'var(--text2)' }}
      >
        <div>Showing {showingFrom}–{showingTo} of {sTotal} orders</div>
        {sTotalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => onPageChange?.(sPage - 1)}
              disabled={sPage <= 1}
            >‹ Prev</button>
            <span className="text-mono font-semibold" style={{ padding: '0 8px' }}>
              {sPage} / {sTotalPages}
            </span>
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => onPageChange?.(sPage + 1)}
              disabled={sPage >= sTotalPages}
            >Next ›</button>
          </div>
        )}
        <div style={{ minWidth: 60 }} />
      </div>
    );
  }

  return (
    <>
      <Pagination />

      <div className="tw">
        <table>
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <SortTh col="order_date"      label="Date"   style={{ width: 90 }}  sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
              <th style={{ minWidth: 200 }}>Items</th>
              <SortTh col="vendor"          label="Vendor" style={{ width: 160 }} sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
              <SortTh col="order_number"    label="Ref #"  style={{ width: 100 }} sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
              <SortTh col="department_name" label="Dept"   style={{ width: 80 }}  sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
              <SortTh col="status"          label="Status" style={{ width: 90 }}  sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
              <SortTh col="total_cost"      label="Cost"   style={{ width: 90 }}  sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
              <th style={{ width: 36 }} />
            </tr>
          </thead>
          <tbody>
            {sortedOrders.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: 0 }}>
                  <EmptyState
                    icon={
                      <svg viewBox="0 0 32 32" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="6" y="8" width="20" height="20" rx="3" />
                        <path d="M6 14h20M12 4v4M20 4v4" />
                      </svg>
                    }
                    title="No orders match these filters"
                    description="Try clearing filters, or add a new order using the button above."
                    action={onAddOrder ? { label: 'Add order', onClick: onAddOrder } : undefined}
                  />
                </td>
              </tr>
            ) : sortedOrders.map((o, idx) => (
              <OrderRow
                key={o.id}
                order={o}
                index={startIdx + idx + 1}
                currency={currency}
                locale={locale}
                expanded={expandedId === o.id}
                onToggleExpand={onToggleExpand}
                onStatusClick={onStatusClick}
                onMenuClick={onMenuClick}
                auditOpen={auditOrderId === o.id}
                auditLoading={auditLoading}
                auditLog={auditLog}
                onToggleAudit={onToggleAudit}
              />
            ))}
          </tbody>
        </table>
      </div>

      <Pagination />
    </>
  );
}
